import { useState, useRef, useCallback } from 'react';
import { Box, Typography, Button, CircularProgress, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import StarIcon from '@material-ui/icons/Star';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DoneIcon from '@material-ui/icons/Done';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import DirectionsIcon from '@material-ui/icons/Directions';
import TimerIcon from '@material-ui/icons/Timer';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';

const labels = {
  pt: {
    title: 'Comparar Rotas',
    close: 'Fechar',
    confirm: 'Confirmar',
    applied: 'Aplicada',
    hint: 'Clica numa rota para a ver no mapa · Confirmar para terminar',
    select: 'Selecionar',
    recommended: 'Recomendada',
    distance: 'Distância',
    duration: 'Duração',
    fuelCost: 'Combustível',
    tollCost: 'Portagens',
    totalCost: 'Custo Total',
    loadingTolls: 'A calcular portagens...',
    clickToSelect: 'Clique para selecionar esta rota',
  },
  en: {
    title: 'Compare Routes',
    close: 'Close',
    confirm: 'Confirm',
    applied: 'Applied',
    hint: 'Click a route to see it on the map · Confirm to finish',
    select: 'Select',
    recommended: 'Recommended',
    distance: 'Distance',
    duration: 'Duration',
    fuelCost: 'Fuel',
    tollCost: 'Tolls',
    totalCost: 'Total Cost',
    loadingTolls: 'Calculating tolls...',
    clickToSelect: 'Click to select this route',
  },
};

const ROUTE_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#a855f7'];

const useStyles = makeStyles(() => ({
  floatingPanel: {
    position: 'fixed',
    top: 80,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 9999,
    background: 'linear-gradient(180deg, #f0f6ff 0%, #ffffff 40%)',
    borderRadius: 14,
    boxShadow: '0 8px 40px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)',
    border: '1px solid rgba(59,130,246,0.15)',
    minWidth: 380,
    maxWidth: '80vw',
    overflow: 'hidden',
    backdropFilter: 'blur(12px)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    borderBottom: '1px solid #e8eef5',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  body: {
    padding: '8px 12px 12px',
  },
  routeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 10px',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    marginBottom: 4,
    border: '1.5px solid transparent',
    '&:last-child': { marginBottom: 0 },
    '&:hover': {
      background: '#f0f7ff',
    },
  },
  colorDot: {
    width: 12,
    height: 12,
    borderRadius: '50%',
    flexShrink: 0,
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
  routeLabel: {
    minWidth: 100,
    flexShrink: 0,
  },
  infoGrid: {
    display: 'flex',
    flex: 1,
    gap: 4,
    alignItems: 'center',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    padding: '2px 7px',
    borderRadius: 6,
    background: '#f1f5f9',
    whiteSpace: 'nowrap',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(255,255,255,0.8)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    zIndex: 10,
    borderRadius: 14,
  },
}));

function formatDistance(meters) {
  return (meters / 1000).toFixed(0) + ' km';
}

function formatDuration(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h${m > 0 ? ` ${m}m` : ''}`;
  return `${m}m`;
}

function formatCost(value, sym = '€') {
  if (value == null || isNaN(value)) return '...';
  return `${value.toFixed(2)} ${sym}`;
}

function formatTotalCost(route, sym = '€') {
  const fuel = route.fuelCost || 0;
  const toll = route.tollCost || 0;
  const total = fuel + toll;
  if (isNaN(total)) return '...';
  return `${total.toFixed(2)} ${sym}`;
}

function RouteCompareDialog({ open, onClose, onConfirm, routes, bestRouteIdx, selectedIdx, lang, onSelectRoute, onHoverRoute, loading, currencySymbol = '€' }) {
  const classes = useStyles();
  const l = labels[lang] || labels.pt;
  const [hoveredIdx, setHoveredIdx] = useState(null);

  // ── Arrastar o painel (pega pelo cabeçalho) ──
  const panelRef = useRef(null);
  const drag = useRef(null);
  const [pos, setPos] = useState(null); // {left, top} em px depois de arrastado
  const onMove = useCallback((e) => {
    if (!drag.current) return;
    const left = Math.max(6, Math.min(window.innerWidth - 120, drag.current.startLeft + (e.clientX - drag.current.mx)));
    const top = Math.max(6, Math.min(window.innerHeight - 60, drag.current.startTop + (e.clientY - drag.current.my)));
    setPos({ left, top });
  }, []);
  const onUp = useCallback(() => {
    drag.current = null;
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
  }, [onMove]);
  const onDown = (e) => {
    const rect = panelRef.current?.getBoundingClientRect();
    const startLeft = pos ? pos.left : (rect ? rect.left : 0);
    const startTop = pos ? pos.top : (rect ? rect.top : 0);
    drag.current = { mx: e.clientX, my: e.clientY, startLeft, startTop };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  if (!open || !routes || routes.length === 0) return null;

  return (
    <Box className={classes.floatingPanel} ref={panelRef}
      style={pos ? { left: pos.left, top: pos.top, transform: 'none' } : undefined}>
      {/* Loading overlay */}
      {loading && (
        <Box className={classes.loadingOverlay}>
          <CircularProgress size={28} style={{ color: '#3b82f6' }} />
          <Typography style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500 }}>
            {l.loadingTolls}
          </Typography>
        </Box>
      )}

      {/* Header — pega de arrasto no título/ícone */}
      <Box className={classes.header}>
        <Box className={classes.headerLeft} onMouseDown={onDown} style={{ cursor: 'grab', userSelect: 'none' }}>
          <DragIndicatorIcon style={{ color: '#cbd5e1', fontSize: 16, marginRight: -2 }} />
          <DirectionsIcon style={{ color: '#3b82f6', fontSize: 18 }} />
          <Typography style={{ fontSize: '0.8rem', fontWeight: 700, color: '#0f172a' }}>
            {l.title}
          </Typography>
          <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 500 }}>
            ({routes.length} {lang === 'en' ? 'routes' : 'rotas'})
          </Typography>
        </Box>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Button
            size="small"
            onClick={onConfirm}
            startIcon={<DoneIcon style={{ fontSize: 14 }} />}
            style={{
              fontSize: '0.65rem', color: '#fff', textTransform: 'none',
              fontWeight: 700, borderRadius: 8, background: 'linear-gradient(135deg, #22c55e, #16a34a)',
              padding: '4px 14px', minWidth: 0, boxShadow: '0 2px 6px rgba(34,197,94,0.35)',
            }}
          >
            {l.confirm}
          </Button>
          <Button
            size="small"
            onClick={onClose}
            startIcon={<CloseIcon style={{ fontSize: 12 }} />}
            style={{
              fontSize: '0.65rem', color: '#64748b', textTransform: 'none',
              fontWeight: 600, borderRadius: 8, border: '1px solid #e2e8f0',
              padding: '3px 10px', background: '#fff', minWidth: 0,
            }}
          >
            {l.close}
          </Button>
        </Box>
      </Box>

      {/* Dica de utilização */}
      <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 500, padding: '2px 14px 0' }}>
        {l.hint}
      </Typography>

      {/* Routes */}
      <Box className={classes.body}>
        {routes.map((route, idx) => {
          const isBest = idx === bestRouteIdx;
          const isSelected = idx === selectedIdx;
          const isHovered = hoveredIdx === idx;
          const color = ROUTE_COLORS[idx] || '#64748b';

          return (
            <Tooltip key={idx} title={l.clickToSelect} placement="left" arrow>
              <Box
                className={classes.routeRow}
                style={{
                  border: isSelected ? `2px solid ${color}` : isBest ? `1.5px solid ${color}` : isHovered ? `1.5px solid ${color}80` : '1.5px solid transparent',
                  background: isSelected ? `${color}14` : isBest ? `${color}08` : isHovered ? '#f0f7ff' : undefined,
                  boxShadow: isSelected ? `0 0 0 3px ${color}22` : undefined,
                }}
                onMouseEnter={() => {
                  setHoveredIdx(idx);
                  if (onHoverRoute) onHoverRoute(idx);
                }}
                onMouseLeave={() => {
                  setHoveredIdx(null);
                  if (onHoverRoute) onHoverRoute(null);
                }}
                onClick={() => onSelectRoute && onSelectRoute(idx)}
              >
                {/* Color dot */}
                <Box className={classes.colorDot} style={{ background: color }} />

                {/* Label */}
                <Box className={classes.routeLabel}>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Typography style={{ fontSize: '0.7rem', fontWeight: 700, color: '#0f172a' }}>
                      {route.label || `Alternativa ${idx}`}
                    </Typography>
                    {isBest && (
                      <StarIcon style={{ fontSize: 12, color: '#f59e0b' }} />
                    )}
                  </Box>
                  {isBest && (
                    <Typography style={{ fontSize: '0.5rem', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      {l.recommended}
                    </Typography>
                  )}
                  {isSelected && (
                    <Typography style={{ fontSize: '0.5rem', fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      ● {l.applied}
                    </Typography>
                  )}
                </Box>

                {/* Info pills */}
                <Box className={classes.infoGrid}>
                  <Box className={classes.infoItem}>
                    <DirectionsIcon style={{ fontSize: 11, color: '#3b82f6' }} />
                    <Typography style={{ fontSize: '0.65rem', fontWeight: 600, color: '#334155' }}>
                      {formatDistance(route.distance)}
                    </Typography>
                  </Box>
                  <Box className={classes.infoItem}>
                    <TimerIcon style={{ fontSize: 11, color: '#f59e0b' }} />
                    <Typography style={{ fontSize: '0.65rem', fontWeight: 600, color: '#334155' }}>
                      {formatDuration(route.duration)}
                    </Typography>
                  </Box>
                  <Box className={classes.infoItem}>
                    <LocalGasStationIcon style={{ fontSize: 11, color: '#ef4444' }} />
                    <Typography style={{ fontSize: '0.65rem', fontWeight: 600, color: '#334155' }}>
                      {formatCost(route.fuelCost, currencySymbol)}
                    </Typography>
                  </Box>
                  <Box className={classes.infoItem}>
                    <MonetizationOnIcon style={{ fontSize: 11, color: '#8b5cf6' }} />
                    <Typography style={{ fontSize: '0.65rem', fontWeight: 600, color: '#334155' }}>
                      {formatCost(route.tollCost, currencySymbol)}
                    </Typography>
                  </Box>
                  <Box className={classes.infoItem} style={{ background: isBest ? '#dcfce7' : '#e0f2fe' }}>
                    <AccountBalanceWalletIcon style={{ fontSize: 11, color: isBest ? '#16a34a' : '#0284c7' }} />
                    <Typography style={{ fontSize: '0.65rem', fontWeight: 800, color: isBest ? '#15803d' : '#0369a1' }}>
                      {formatTotalCost(route, currencySymbol)}
                    </Typography>
                  </Box>
                </Box>

                {/* Indicador de seleção — persistente na rota aplicada, ou no hover */}
                {(isSelected || isHovered) && (
                  <CheckCircleIcon style={{ fontSize: 18, color, flexShrink: 0 }} />
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

export default RouteCompareDialog;
